import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Wrench, MapPin } from "lucide-react";
import { money, VerifiedBadge } from "@/components/marketplace/shared";
import { Rating } from "@/components/marketplace/Reviews";
import { FavoriteButton } from "@/components/FavoriteButton";
import { FeaturedBadge } from "@/components/featured/FeaturedBadge";
import { useFeaturedImpression, featuredClickProps } from "@/components/featured/featuredMetrics";
import { useTour } from "@/components/Onboarding";

export const DELIVERY_LABEL = { in_person: "Presencial", online: "Online", both: "Presencial ou online" };

export function ServiceCard({ s }) {
  const ref = useFeaturedImpression("service", s.id, !!s.featured_active);
  return (
    <Link ref={ref} to={`/marketplace/servico/${s.id}`} data-testid={`service-card-${s.id}`} data-featured={s.featured_active ? "1" : "0"} {...featuredClickProps(s, "service")} className="bg-white rounded-2xl border border-zinc-200 overflow-hidden card-hover block">
      <div className="relative aspect-[4/3] bg-zinc-100">{s.images?.[0] && <img src={s.images[0]} alt={s.title} className="w-full h-full object-cover" />}
        {s.featured_active && <FeaturedBadge onDark className="absolute top-2 left-2" testid={`featured-badge-service-${s.id}`} />}</div>
      <div className="p-3">
        <div className="font-semibold text-sm truncate flex items-center justify-between gap-1"><span className="truncate">{s.title}</span><FavoriteButton type="service" id={s.id} /></div>
        <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1"><MapPin size={10} /> {s.location || s.city || s.seller_name}</div>
        {s.seller_verified && <div className="mt-0.5"><VerifiedBadge verified testid={`verified-badge-${s.id}`} /></div>}
        <Rating avg={s.rating_avg} count={s.rating_count} testid={`rating-${s.id}`} />
        <div className="mt-1 font-display font-extrabold text-sm" data-testid={`service-card-price-${s.id}`}>
          {s.service_type === "fixed_price" ? money(s.price_minor, s.currency) : <span className="text-[#c8102e]">Sob consulta</span>}
        </div>
      </div>
    </Link>
  );
}

export default function Services() {
  useTour("buyer_marketplace");
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState(null);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  useEffect(() => { api.get("/marketplace/categories", { params: { kind: "services" } }).then(r => setCats(r.data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/services", { params: { ...(category && { category }), ...(q && { q }), ...(verifiedOnly && { verified_only: true }) } }).then(r => setItems(r.data)), 200);
    return () => clearTimeout(t);
  }, [category, q, verifiedOnly]);
  const setCat = (c) => { const p = new URLSearchParams(params); c ? p.set("category", c) : p.delete("category"); setParams(p); };
  return (
    <div className="page fade-up">
      <div className="pt-4 pb-3 flex items-center gap-2"><Wrench className="text-[#c8102e]" size={22} /><h1 className="font-display text-2xl font-extrabold">Serviços</h1></div>
      <div className="flex gap-2 mb-3 text-[11px] font-semibold">
        <Link to="/marketplace" data-testid="svc-nav-products" className="px-3 py-1.5 rounded-full border border-zinc-200 bg-white">Produtos</Link>
        <span className="px-3 py-1.5 rounded-full bg-[#c8102e] text-white">Serviços</span>
      </div>
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input data-testid="svc-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Procurar serviços…" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm outline-none focus:border-black" />
      </div>
      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-3">
        <button onClick={() => setCat("")} className="chip whitespace-nowrap" data-active={!category}>Todos</button>
        <button data-testid="svc-filter-verified" onClick={() => setVerifiedOnly(v => !v)} className="chip whitespace-nowrap" data-active={verifiedOnly}>✓ Só verificadas</button>
        {cats.map(c => <button key={c.id} onClick={() => setCat(c.name)} className="chip whitespace-nowrap" data-active={category === c.name}>{c.name}</button>)}
      </div>
      {items === null && <p className="text-sm text-zinc-500 text-center py-8">A carregar…</p>}
      {items?.length === 0 && <p data-testid="svc-empty" className="text-center py-10 text-zinc-500 text-sm">Ainda não há serviços publicados{category ? ` em ${category}` : ""}.</p>}
      <div className="grid grid-cols-2 gap-3 mb-8">{items?.map(s => <ServiceCard key={s.id} s={s} />)}</div>
    </div>
  );
}
